import React, { forwardRef, useMemo, useRef, useState } from "react";
import styled, { withTheme } from "styled-components";
import DatePicker from "react-datepicker";
import es from "date-fns/locale/es";
import it from "date-fns/locale/it";
import fr from "date-fns/locale/fr";
import enUS from "date-fns/locale/en-US";
import frCA from "date-fns/locale/fr-CA";
import de from "date-fns/locale/de";
import nl from "date-fns/locale/nl";

import DateSelectOption from "./DateSelectOption";

import AlertUGNX from "../../../ToastsAndAlerts/AlertUGNX";
import { AlertType } from "../../../ToastsAndAlerts/constants";
import { getCurrentLocale } from "../../../../i18n/helpers";
import { useTranslation } from "react-i18next";
import { DateFormat } from "../../../../modules/constants";
import moment from "moment";

const StyledContainer = styled.div`
  .date-picker-wrapper {
    width: auto;
    flex: 1 1 auto !important;
    display: inline-block;
    padding: 0;
    border: 0;
  }

  .react-datepicker__input-container {
    position: relative;
    display: inline-block;
    width: 100%;
  }

  .react-datepicker__day--in-range,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--keyboard-selected,
  .react-datepicker__day--selected,
  .react-datepicker__header,
  .react-datepicker__month-text--in-range,
  .react-datepicker__month-text--in-selecting-range,
  .react-datepicker__month-text--keyboard-selected,
  .react-datepicker__month-text--selected,
  .react-datepicker__quarter-text--in-range,
  .react-datepicker__quarter-text--in-selecting-range,
  .react-datepicker__quarter-text--keyboard-selected,
  .react-datepicker__quarter-text--selected {
    background-color: ${(props) => props?.theme?.darkPurple};
  }

  .react-datepicker-time__header,
  .react-datepicker-year-header,
  .react-datepicker__current-month,
  .react-datepicker__day-name,
  .react-datepicker__navigation--next,
  .react-datepicker__navigation--previous,
  .react-datepicker__time-name {
    color: ${(props) => props?.theme?.white};
  }

  .react-datepicker__month-read-view--selected-month,
  .react-datepicker__year-read-view--selected-year {
    color: ${(props) => props?.theme?.white};
  }

  .react-datepicker-popper[data-placement^="top"] .react-datepicker__triangle,
  .react-datepicker__month-read-view--down-arrow,
  .react-datepicker__month-read-view--down-arrow:before,
  .react-datepicker__month-year-read-view--down-arrow,
  .react-datepicker__month-year-read-view--down-arrow:before,
  .react-datepicker__year-read-view--down-arrow,
  .react-datepicker__year-read-view--down-arrow:before {
    border-bottom: none;
    border-top-color: ${(props) => props?.theme?.white};
  }

  .react-datepicker-popper[data-placement^="bottom"]
    .react-datepicker__triangle {
    border-top: none;
    border-bottom-color: ${(props) => props?.theme?.white};
  }

  .react-datepicker__navigation--next,
  .react-datepicker__navigation--next:hover {
    border-left-color: ${(props) => props?.theme?.white};
  }

  .react-datepicker__navigation--previous,
  .react-datepicker__navigation--previous:hover {
    border-right-color: ${(props) => props?.theme?.white};
  }

  .multi-month-calendar {
    border-radius: 0;
    border: 0;
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16);
  }

  .multi-month-calendar .react-datepicker__header {
    border-radius: 0;
  }

  .react-datepicker__month {
    margin: 0rem 0.4rem;
  }

  .react-datepicker__day--in-range {
    background-color: ${(props) => props?.theme?.lightPurple};
  }

  .react-datepicker__day--range-start,
  .react-datepicker__day--range-end {
    background-color: ${(props) => props?.theme?.darkPurple};
  }

  .react-datepicker__month-container {
    @media (max-width: 932px) {
      float: none;
    }
  }
  .react-datepicker-popper {
    @media (max-width: 770px) {
      transform: none !important;
      top: auto !important;
      left: auto !important;
    }
  }
`;

const StyledInputDatePicker = styled.div`
  border: 0;
  border-radius: 0;
  background-color: ${(props) => props?.theme?.white};
  color: ${(props) => props?.theme?.fg};
  padding: 0.375rem 2.8rem 0.375rem
    ${(props) => (props.showBefore ? "2.8rem" : ".375rem")};
  margin-right: 20px;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
  border-bottom: 2px solid ${(props) => props?.theme?.dustyGray};
  min-width: ${(props) => (props.showBefore ? "250px" : "150px")};
  height: calc(1.5em + 0.75rem + 2px);
  width: 100%;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;

  &:hover {
    cursor: pointer;
    background-color: ${(props) => props?.theme?.whiteSmoke};
    border-bottom: 2px solid ${(props) => props.theme?.darkPurple} !important;
  }
`;

const StyledInputIcon = styled.i`
  font-size: 1.1rem;
  position: absolute;
  top: 10px;
  right: ${(props) => (props.showBefore ? "auto" : "17px")};
  left: ${(props) => (props.showBefore ? "17px" : "auto")};
  ${(props) => (props.showBefore ? `color: ${props?.theme?.darkPurple}` : "")}
`;

const StyledTitle = styled.h6`
  margin-bottom: 0.25rem;
  font-weight: 700;
  line-height: 1.2;
`;

const StyledRightContent = styled.div`
  position: absolute;
  right: ${(props) =>
    props.rightPosition ? `${props.rightPosition}px` : "-110px"};
  min-height: 100%;
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16);
  background-color: ${(props) => props?.theme?.white};

  @media (max-width: 932px) {
    position: relative;
    max-width: fit-content;
    bottom: -100%;
    right: 0;
    left: 0;
    width: 100%;
    margin: auto;
    min-width: 100%;
    text-align: center;
  }
`;

const InputDatePicker = forwardRef(
  ({ value, onClick, className, showBefore, realDateValue }, _ref) => (
    <>
      <StyledInputDatePicker
        onClick={onClick}
        showBefore={showBefore}
        data-testid="inputDatePickerId"
      >
        {showBefore && (
          <StyledInputIcon showBefore={showBefore} className={className} />
        )}
        {realDateValue}
        <StyledInputIcon
          className={showBefore ? "fas fa-chevron-down" : className}
        />
      </StyledInputDatePicker>
    </>
  )
);

const defaultRangeDateOptions = [
  {
    title: "Today",
    isSingleDay: true,
    daysFromToday: 0,
  },
  {
    title: "Yesterday",
    isSingleDay: true,
    daysFromToday: 1,
  },
  {
    title: "Last 3 days",
    isSingleDay: false,
    daysFromToday: 3,
  },
  {
    title: "Last 7 days",
    isSingleDay: false,
    daysFromToday: 7,
  },
  {
    title: "Last 30 days",
    isSingleDay: false,
    daysFromToday: 30,
  },
  {
    title: "Last 90 days",
    isSingleDay: false,
    daysFromToday: 90,
  },
];

/**
 *  ** Date Select component
 *  @param {Object} theme The theme object to be used by the component
 *  @param {Date} [maxDate] Max selectable date
 *  @param {Date} [minDate] Min selectable date
 *  @param {Function} [onChange] The onchange callback function
 *  @param {Date} eventDate Selected event date
 *  @param {Function} setEventDate Setter for the selected event date
 *  @param {Date} [startDate] Selected start date - used on range select
 *  @param {Function} [setStartDate] Setter for the selected start date - used on range select
 *  @param {Date} [endDate] Selected end date - used on range select
 *  @param {Function} [setEndDate] Setter for the selected end date - used on range select
 *  @param {Boolean} [selectsRange] If true will allow for range selection. If false will only be single selection
 *  @param {Object} errorHandling The errorHandling object to be used by the component
 *  @param {Boolean} errorHandling.showErrorDate If true will display error message
 *  @param {Function} errorHandling.setShowErrorDate Setter for showErrorDate
 *  @param {Boolean} errorHandling.isValidDate True if has valid data
 *  @param {Function} errorHandling.setIsValidDate Setter for isValidDate
 *  @param {Object} [popperModifiers] Date picker Poppermodifiers object, check react date picker doc
 *  @param {String} [dateFormat="dd/MMM/yyyy"] The date format
 *  @param {String} [id="default-date-picker-id-div"] Component wrapper html id
 *  @param {Boolean} [displayTitle = true] If true will display "date" title before input
 *  @param {String} [inputTitle] It will display alternative date title before input
 *  @param {String} [errorMessage = "Invalid Date."] Error message to display when field has error
 *  @param {Object[]} [rangeDateOptions] The options to be added / shown on right of the calendar
 *  @param {String} title The option title to be displayed
 *  @param {Boolean} isSingleDay True if the option is a single day selection, false will be a range
 *  @param {Number} daysFromToday If single day the number of days to be subtracted from today. If range, the number of days to be selected before today.
 *  @param {Boolean} displayDateOptions True to display the available options on right of the calendar
 */
const DateSelect = (props) => {
  const {
    maxDate,
    minDate,
    onChange,
    eventDate,
    setEventDate,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectsRange,
    errorHandling,
    popperModifiers,
    dateFormat = "dd/MMM/yyyy",
    id = "default-date-picker-id-div",
    displayTitle = true,
    inputTitle,
    errorMessage = "Invalid Date.",
    monthsShown = 1,
    rangeDateOptions = defaultRangeDateOptions,
    displayDateOptions = false,
    readOnly,
    onCalendarClose,
    modifiers,
    filterDate,
    dayClassName,
    onDayMouseEnter,
    showMonthYearPicker = false,
    showFullMonthYearPicker = false,
    showYearPicker = false,
    onCalendarOpen,
    showMonthDropdown,
    showYearDropdown,
  } = props;

  const { t } = useTranslation();

  const { showErrorDate, setShowErrorDate, setIsValidDate } = errorHandling;

  const [currentOption, setCurrentOption] = useState(-1);

  const datePickerRef = useRef(null);

  const handleDateChange = (date) => {
    setCurrentOption(-1);
    if (selectsRange) {
      const [start, end] = date;
      setStartDate(start);
      setEventDate(start);
      setEndDate(end);

      if (end && datePickerRef && datePickerRef.current) {
        datePickerRef.current.handleCalendarClickOutside();
      }
    } else {
      setEventDate(date);
    }

    let isValid = date !== "";

    if (isValid) {
      if (onChange) {
        onChange(date);
      }

      setIsValidDate(isValid);
      setShowErrorDate(!isValid);
    }
  };

  const ref = React.createRef();

  const isSingleMonth = () => {
    return monthsShown < 2;
  };

  const getDateValue = () => {
    if (dateFormat !== "dd/MMM/yyyy") {
      return eventDate && eventDate !== ""
        ? moment(eventDate).format(dateFormat)
        : eventDate
        ? eventDate
        : "";
    }
    const startDateStr =
      eventDate && eventDate !== ""
        ? moment(eventDate).format(DateFormat.BASE_FORMAT)
        : eventDate
        ? eventDate
        : "";
    const endDateStr =
      selectsRange && endDate
        ? ` - ${moment(endDate).format(DateFormat.BASE_FORMAT)}`
        : "";

    return `${startDateStr}${endDateStr}`;
  };

  const onOptionClick = (index) => {
    setCurrentOption(index);
    datePickerRef.current.handleCalendarClickOutside();
  };

  const locale = useMemo(() => {
    const lang = getCurrentLocale();

    if (lang === "es" || lang === "es-US") {
      return es;
    }

    if (lang === "fr") {
      return fr;
    }

    if (lang === "fr-CA") {
      return frCA;
    }

    if (lang === "it") {
      return it;
    }

    if (lang === "de") {
      return de;
    }

    if (lang === "nl") {
      return nl;
    }

    return enUS;
  }, []);

  const rightPanelPosition = useMemo(() => {
    const lang = getCurrentLocale();
    if (lang === "fr" || lang === "fr-CA" || lang === "es") {
      return -135;
    }

    if (lang === "es-US" || lang === "it") {
      return -120;
    }

    if (lang === "de") {
      return -145;
    }

    return -105;
  }, []);

  return (
    <StyledContainer data-testid="dateSelectId">
      {displayTitle && (
        <StyledTitle>{inputTitle ?? `${t("appText.date")}:`}</StyledTitle>
      )}
      <div id={id}>
        <DatePicker
          onDayMouseEnter={onDayMouseEnter}
          dayClassName={dayClassName}
          filterDate={filterDate}
          ref={datePickerRef}
          locale={locale}
          readOnly={readOnly}
          dateFormat={dateFormat}
          showYearDropdown={showYearDropdown}
          showMonthDropdown={showMonthDropdown}
          selected={eventDate}
          onChange={(date) => handleDateChange(date)}
          maxDate={maxDate}
          minDate={minDate}
          customInput={
            <InputDatePicker
              realDateValue={getDateValue()}
              className={
                isSingleMonth() ? "far fa-calendar" : "far fa-calendar-alt"
              }
              showBefore={!isSingleMonth()}
              ref={ref}
            />
          }
          onCalendarClose={onCalendarClose}
          wrapperClassName="date-picker-wrapper"
          monthsShown={monthsShown}
          showPopperArrow={isSingleMonth()}
          calendarClassName={isSingleMonth() ? null : "multi-month-calendar"}
          startDate={startDate}
          endDate={endDate}
          selectsRange={selectsRange}
          disabledKeyboardNavigation
          shouldCloseOnSelect={!selectsRange}
          popperModifiers={popperModifiers}
          modifiers={modifiers}
          showMonthYearPicker={showMonthYearPicker}
          showFullMonthYearPicker={showFullMonthYearPicker}
          showYearPicker={showYearPicker}
          onCalendarOpen={onCalendarOpen}
        >
          {displayDateOptions && (
            <StyledRightContent rightPosition={rightPanelPosition}>
              {rangeDateOptions?.map((item, index) => {
                return (
                  <DateSelectOption
                    key={`date_select_${index}`}
                    singleDay={item.isSingleDay}
                    daysFromToday={item.daysFromToday}
                    setEventDate={setEventDate}
                    setStartDate={setStartDate}
                    setEndDate={(date) => setEndDate(date)}
                    onOptionClick={() => onOptionClick(index)}
                    className={index === currentOption ? "active" : ""}
                  >
                    {item.title}
                  </DateSelectOption>
                );
              })}
            </StyledRightContent>
          )}
        </DatePicker>

        {showErrorDate && (
          <AlertUGNX type={AlertType.DANGER}>{errorMessage}</AlertUGNX>
        )}
      </div>
    </StyledContainer>
  );
};

export default withTheme(DateSelect);
